// Main-thread client for the engine worker. Spawns one worker, ships the captured
// frame + layout map to it, and resolves with the RawResponse. The frame buffer
// and the returned overlay buffer are transferred (zero-copy).
import type { LayoutMap, RawResponse, EngineOptions } from '../engine/types.js';

export interface SheetQuad {
  quad: Array<{ x: number; y: number }>;
  width: number;
  height: number;
}

let worker: Worker | null = null;
let seq = 0;
const pending = new Map<number, { resolve: (r: any) => void; reject: (e: Error) => void }>();

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL('../engine/engine.worker.ts', import.meta.url));
  worker.onmessage = (e: MessageEvent) => {
    const { id, ok, raw, quad, error } = e.data;
    const pr = pending.get(id);
    if (!pr) return;
    pending.delete(id);
    if (ok) pr.resolve(raw !== undefined ? raw : quad);
    else pr.reject(new Error(error));
  };
  worker.onerror = (e) => {
    const err = new Error('engine worker error: ' + e.message);
    pending.forEach((p) => p.reject(err));
    pending.clear();
  };
  return worker;
}

function cvUrl(): string {
  return new URL(`${import.meta.env.BASE_URL}vendor/opencv.js`, location.href).href;
}

/** Fast detect-only pass for the live alignment indicator. Returns the ordered
 *  corner quad in the preview frame's pixel space, or null if <4 markers. */
export function detectInWorker(image: ImageData): Promise<SheetQuad | null> {
  const w = ensureWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    w.postMessage(
      { id, kind: 'detect', cvUrl: cvUrl(), image: { data: image.data, width: image.width, height: image.height } },
      [image.data.buffer],
    );
  });
}

export function gradeInWorker(
  image: ImageData,
  layout: LayoutMap,
  options: Partial<EngineOptions> = {},
): Promise<RawResponse> {
  const w = ensureWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    const payload = {
      id,
      kind: 'grade',
      cvUrl: cvUrl(),
      image: { data: image.data, width: image.width, height: image.height },
      layout,
      options,
    };
    w.postMessage(payload, [image.data.buffer]);
  });
}
